import { Form, Input, Button, App as AntdApp } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useResetPassword } from '../../hooks/useAuth';
import { encryptPassword } from '../../utils/decrypt';

const ResetPassword = () => {
  const { message } = AntdApp.useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const { mutateAsync: resetPassword, isPending } = useResetPassword();

  // Get email from navigation state
const token = location.state?.resetToken;
  const onFinish = async (values: { password: string; confirmPassword: string }) => {
    if (values.password !== values.confirmPassword) {
      message.error('Password and confirm password do not match');
      return;
    }

if (!token) {
  message.error('Invalid reset session. Please restart the password reset process.');
  navigate('/forgot-password');
  return;
}

   const encryptedPassword = encryptPassword(values.password);

    try {
      await resetPassword({ token, newPassword: encryptedPassword });
      message.success('Password reset successfully!');
      navigate('/login');
    } catch (error: unknown) {
      console.error('Reset password error:', error);
      const errorMessage =
        (error as { message?: string; error?: string })?.message ||
        (error as { message?: string; error?: string })?.error ||
        'Failed to reset password. Please try again.';
      message.error(errorMessage);
    }
  };

  return (
    <Form
      name="reset-password"
      layout="vertical"
      onFinish={onFinish}
      autoComplete="off"
      requiredMark={true}
      size="large"
      className="p-2"
    >
      <Form.Item
        label={<span className="text-sm font-medium text-gray-700">New Password</span>}
        name="password"
        rules={[
          { required: true, message: 'Please input your new password!' },
          { min: 8, message: 'Password must be at least 8 characters!' },
          {
            pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
            message: 'Password must contain uppercase, lowercase, number and special character!',
          },
        ]}
      >
        <Input.Password
          className="rounded-lg bg-gray-50 py-3"
          placeholder="Enter your new password"
        />
      </Form.Item>

      <Form.Item
        label={<span className="text-sm font-medium text-gray-700">Confirm Password</span>}
        name="confirmPassword"
        dependencies={['password']}
        rules={[
          { required: true, message: 'Please confirm your password!' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error('The two passwords do not match!'));
            },
          }),
        ]}
      >
        <Input.Password
          className="rounded-lg bg-gray-50 py-3"
          placeholder="Confirm your new password"
        />
      </Form.Item>

      <Form.Item className="mt-6">
        <Button
          type="primary"
          htmlType="submit"
          loading={isPending}
          className=" text-white w-full py-2 h-auto font-medium text-base flex items-center justify-center cursor-pointer border-none shadow-none"
          disabled={isPending}
        >
          {isPending ? 'Setting Password...' : 'Set New Password'}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default ResetPassword;
